import { Add, Delete, Edit, EmojiPeople } from '@mui/icons-material';
import { Box, AppBar, Button, Divider, Grid, Tab, Tabs, Theme, useMediaQuery } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { FC, useEffect, useMemo, useState, useContext } from 'react';
import { ManageVendorPageContext } from '../../../../context';
import {
  Button as SMButton,
  Loader,
  Page,
  PageTitleWithBack,
  Pagination,
  Table
} from '../../../../components';
import {
  deleteDocumentType,
  deleteSubcontractorType,
  getDocumentTypes,
  getVendors,
  getAdminSubcontractorsTypes,
  getVendorTypes
} from '../../../../fetch';
import { formatInputPhoneNumber } from '../../../../helpers';
import { IVendor, IDocumentType, ISubcontractorType, IVendorType } from '../../../../models';
import { MobileVendor } from './MobileVendor';
import { DocumentTypeModal } from './VendorDocumentTypeModal';
import { SubcontractorTypeModal } from './SubcontractorTypeModal';
import { PartnersFilters } from './PartnersFilters';

interface IManageVendorsList {}

export const ManageVendorsList: FC<IManageVendorsList> = () => {
  const classes = useStyles();
  // context
  const { enqueueSnackbar } = useSnackbar();
  const {
    setSortDirection,
    setSelectedSort,
    setSearchValue,
    setSearchedValue,
    setSelectedVendorTypeIds,
    setSelectedTypeId,
    setRowsPerPage,
    setRecordCount,
    setPage,
    setHasAppliedFilters,
    hasAppliedFilters,
    selectedSort,
    sortDirection,
    page,
    recordCount,
    perPage,
    selectedTypeId,
    selectedVendorTypeIds,
    searchedValue,
    searchValue,
    filters,
    setFilters
  } = useContext(ManageVendorPageContext);
  // state
  const [isDeleting, setDeleting] = useState<boolean>(false);
  const [isLoadingVendors, setLoadingVendors] = useState<boolean>(false);
  const [isLoadingVendorTypes, setLoadingVendorTypes] = useState<boolean>(false);
  const [isLoadingSubcontractorTypes, setLoadingSubcontractorTypes] = useState<boolean>(false);
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [currentSubcontractorType, setCurrentSubcontractorType] =
    useState<ISubcontractorType | null>(null);
  const [vendors, setVendors] = useState<IVendor[]>([]);
  const [subcontractorTypes, setSubcontractorTypes] = useState<ISubcontractorType[]>([]);
  const [vendorTypes, setVendorTypes] = useState<IVendorType[]>([]);
  const [allSubcontractorTypes, setAllSubcontractorTypes] = useState<ISubcontractorType[]>([]);
  const [typePage, setTypePage] = useState<number>(0);
  const [recordTypeCount, setRecordTypeCount] = useState<number>(0);
  const [typePerPage, setTypeRowsPerPage] = useState<number>(10);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [isLoadingDocumentTypes, setLoadingDocumentTypes] = useState(false);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [recordDocumentTypeCount, setRecordDocumentTypeCount] = useState(0);
  const [documentTypePage, setDocumentTypePage] = useState(0);
  const [documentTypePerPage, setDocumentTypePerPage] = useState(10);
  const [isDocumentTypeModalOpen, setDocumentTypeModalOpen] = useState<boolean>(false);

  const history = useHistory();
  const isMobile = useMediaQuery('(max-width: 960px)');

  const fetchVendors = async () => {
    try {
      setLoadingVendors(true);
      const res = await getVendors({
        page: page + 1,
        perPage,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        subcontractorTypeIds: selectedTypeId,
        vendorTypeIds: selectedVendorTypeIds as any[],
        name: searchedValue
      });
      setRecordCount(res.totalRecordCount);
      setVendors(res.records);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Error loading vendors, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoadingVendors(false);
    }
  };

  const fetchAllVendorTypes = async () => {
    try {
      setLoadingVendorTypes(true);
      const res = await getVendorTypes();
      setVendorTypes(res.records);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Error loading vendor types, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoadingVendorTypes(false);
    }
  };

  const fetchAllSubcontractorTypes = async () => {
    try {
      setLoadingSubcontractorTypes(true);
      const res = await getAdminSubcontractorsTypes();
      setAllSubcontractorTypes(res.records);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Error loading subcontractor types, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoadingSubcontractorTypes(false);
    }
  };

  const fetchSubcontractorTypes = async () => {
    try {
      setLoadingSubcontractorTypes(true);
      const res = await getAdminSubcontractorsTypes({
        page: typePage + 1,
        perPage: typePerPage
      });
      setRecordTypeCount(res.totalRecordCount);
      setSubcontractorTypes(res.records);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Error loading subcontractor types, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoadingSubcontractorTypes(false);
    }
  };

  const fetchDocumentTypes = async () => {
    try {
      setLoadingDocumentTypes(true);
      const res = await getDocumentTypes({
        page: documentTypePage + 1,
        perPage: documentTypePerPage
      });
      setRecordDocumentTypeCount(res.totalRecordCount);
      setDocumentTypes(res.records);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Error loading document types, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoadingDocumentTypes(false);
    }
  };
  useEffect(() => {
    fetchAllSubcontractorTypes();
    fetchAllVendorTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (selectedTab === 0) {
      // Reset all other tab's values
      setTypePage(0);
      setRecordTypeCount(0);
      setTypeRowsPerPage(10);
      setDocumentTypePage(0);
      setRecordDocumentTypeCount(0);
      setDocumentTypePerPage(10);
      fetchVendors();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, sortDirection, selectedSort, selectedTab, filters, searchedValue]);

  useEffect(() => {
    if (selectedTab === 1) {
      // Reset all other tab's values
      setPage(0);
      setRecordCount(0);
      setRowsPerPage(10);
      setDocumentTypePage(0);
      setRecordDocumentTypeCount(0);
      setDocumentTypePerPage(10);
      fetchSubcontractorTypes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, typePage, typePerPage]);

  useEffect(() => {
    if (selectedTab === 2) {
      // Reset all other tab's values
      setPage(0);
      setRecordCount(0);
      setRowsPerPage(10);
      setTypePage(0);
      setRecordTypeCount(0);
      setTypeRowsPerPage(10);

      fetchDocumentTypes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, documentTypePage, documentTypePerPage]);

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const vendorColumns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        isServerSorted: selectedSort === 'Name',
        isServerSortedDesc: sortDirection.Name === 'Desc',
        handleClickColumn: () => handleClickColumn('Name')
      },
      {
        Header: 'Vendor Type',
        accessor: 'vendorType',
        isServerSorted: selectedSort === 'VendorType',
        isServerSortedDesc: sortDirection.VendorType === 'Desc',
        handleClickColumn: () => handleClickColumn('VendorType')
      },
      {
        Header: 'Subcontractor Type',
        accessor: 'subcontractorType',
        isServerSorted: selectedSort === 'SubcontractorType',
        isServerSortedDesc: sortDirection.SubcontractorType === 'Desc',
        handleClickColumn: () => handleClickColumn('SubcontractorType')
      },
      {
        Header: 'Sage ID',
        accessor: 'sageId',
        isServerSorted: selectedSort === 'SageId',
        isServerSortedDesc: sortDirection.SageId === 'Desc',
        handleClickColumn: () => handleClickColumn('SageId')
      },
      {
        Header: 'Users',
        accessor: 'userCount',
        isServerSorted: selectedSort === 'UserCount',
        isServerSortedDesc: sortDirection.UserCount === 'Desc',
        handleClickColumn: () => handleClickColumn('UserCount'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IVendor } };
        }) => {
          return (
            <Box display="flex">
              <EmojiPeople />
              {`(${original.userCount || 0})`}
            </Box>
          );
        }
      },
      {
        Header: 'Phone Number',
        accessor: 'phoneNumber',
        isServerSorted: selectedSort === 'Phone',
        isServerSortedDesc: sortDirection.Phone === 'Desc',
        handleClickColumn: () => handleClickColumn('Phone'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IVendor } };
        }) => {
          return <>{formatInputPhoneNumber(original.phoneNumber)}</>;
        }
      },
      {
        Header: 'Active',
        accessor: 'isActive',
        isServerSorted: selectedSort === 'IsActive',
        isServerSortedDesc: sortDirection.IsActive === 'Desc',
        handleClickColumn: () => handleClickColumn('IsActive'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IVendor } };
        }) => {
          return <>{original.isActive ? 'Yes' : 'No'}</>;
        }
      },
      {
        id: 'actions',
        Header: '',
        accessor: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IVendor } };
        }) => {
          return (
            <div className={classes.actionButtons}>
              <Button
                color="primary"
                startIcon={<Edit />}
                className={classes.editButton}
                onClick={() => {
                  history.push(`/admin/vendor-tools/vendors/${original.vendorId}`);
                }}
              >
                Edit
              </Button>
            </div>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendors]);

  const subcontractorsTypesColumns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        sort: false
      },

      {
        id: 'actions',
        Header: '',
        accessor: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ISubcontractorType } };
        }) => {
          return (
            <div className={classes.actionButtonTypes}>
              <Button
                color="primary"
                startIcon={<Edit />}
                onClick={() => {
                  setModalOpen(true);
                  setCurrentSubcontractorType(original);
                }}
              >
                Edit
              </Button>
              <Button
                color="inherit"
                startIcon={<Delete />}
                className={classes.deleteButton}
                onClick={async () => {
                  const result = window.confirm(
                    'Are you sure you want to delete this subcontractor type?'
                  );
                  if (result) {
                    try {
                      setDeleting(true);
                      await deleteSubcontractorType(original.subcontractorTypeId);
                      enqueueSnackbar('Subcontractor Type Deleted!', {
                        variant: 'success'
                      });
                      fetchSubcontractorTypes();
                    } catch (error) {
                      console.log(error);
                      enqueueSnackbar('Error deleting subcontractor type, please try again', {
                        variant: 'error'
                      });
                    } finally {
                      setDeleting(false);
                    }
                  }
                }}
              >
                Delete
              </Button>
            </div>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subcontractorTypes]);

  const [currentDocumentType, setCurrentDocumentType] = useState(null);
  const documentTypeColumns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        sort: false
      },

      {
        id: 'actions',
        Header: '',
        accessor: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IDocumentType } };
        }) => {
          return (
            <div className={classes.actionButtonTypes}>
              <Button
                color="primary"
                startIcon={<Edit />}
                onClick={() => {
                  setDocumentTypeModalOpen(true);
                  setCurrentDocumentType(original);
                }}
              >
                Edit
              </Button>
              <Button
                color="inherit"
                startIcon={<Delete />}
                className={classes.deleteButton}
                onClick={async () => {
                  const result = window.confirm(
                    'Are you sure you want to delete this document type?'
                  );
                  if (result) {
                    try {
                      setDeleting(true);
                      await deleteDocumentType(original.documentTypeId);
                      enqueueSnackbar('Document Type Deleted!', {
                        variant: 'success'
                      });
                      fetchDocumentTypes();
                    } catch (error) {
                      console.log(error);
                      enqueueSnackbar('Error deleting document type, please try again', {
                        variant: 'error'
                      });
                    } finally {
                      setDeleting(false);
                    }
                  }
                }}
              >
                Delete
              </Button>
            </div>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentTypes]);
  const hasAllAppliedFilters = hasAppliedFilters || searchedValue.length > 0;
  return (
    <>
      {isDeleting && (
        <Loader position="centered" type="fullscreen">
          Loading...
        </Loader>
      )}
      <Page title="Manage Vendors">
        <Grid container className={classes.titleWrapper}>
          <Grid item xs={12} className={classes.pageTitleWrapper}>
            <PageTitleWithBack
              showDivider={false}
              backText="Back to Admin"
              isExternalLink={false}
              backUrl={'/admin'}
              title="Manage Vendors"
            />
          </Grid>
          <Grid item>
            <Box display="flex">
              {selectedTab === 0 && (
                <>
                  <SMButton
                    disabled={isLoadingVendors}
                    id="create-vendor"
                    startIcon={<Add />}
                    onClick={() => history.push('/admin/vendor-tools/vendors/add')}
                    color="primary"
                    variant="contained"
                    className={classes.addButton}
                  >
                    Add Vendor
                  </SMButton>
                </>
              )}
              {selectedTab === 1 &&
                // DB constraint, can't create more than 99 subcontractor types
                recordTypeCount !== 99 && (
                  <SMButton
                    disabled={isLoadingSubcontractorTypes}
                    id="create-new-type"
                    startIcon={<Add />}
                    onClick={() => setModalOpen(true)}
                    color="primary"
                    variant="contained"
                    className={classes.addDocumentButton}
                  >
                    Add Subcontractor Type
                  </SMButton>
                )}
              {selectedTab === 2 && (
                <SMButton
                  disabled={isLoadingDocumentTypes}
                  id="create-new-type"
                  startIcon={<Add />}
                  onClick={() => setDocumentTypeModalOpen(true)}
                  color="primary"
                  variant="contained"
                  className={classes.addDocumentButton}
                >
                  Add Document Type
                </SMButton>
              )}
            </Box>
          </Grid>
        </Grid>
        <Divider />

        <AppBar position="static" color="default" component="div" className={classes.tabBar}>
          <Tabs
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile}
            allowScrollButtonsMobile={isMobile}
            value={selectedTab}
            indicatorColor="primary"
            textColor="primary"
            disabled={isLoadingVendors}
            onChange={(_, value) => {
              setSelectedTab(value);
            }}
          >
            <Tab id="vendors" aria-controls="vendors" label="Vendors" />
            <Tab
              id="subcontract-types"
              aria-controls="subcontract-types"
              label="Subcontractor Types"
            />
            <Tab id="document-types" aria-controls="types" label="Document Types" />
          </Tabs>
        </AppBar>
        {selectedTab === 0 && (
          <>
            <PartnersFilters
              setSearchValue={setSearchValue}
              setSearchedValue={setSearchedValue}
              searchValue={searchValue}
              types={allSubcontractorTypes}
              isLoading={isLoadingVendors || isLoadingVendorTypes || isLoadingSubcontractorTypes}
              selectedTypeId={selectedTypeId}
              setSelectedTypeId={setSelectedTypeId}
              vendorTypes={vendorTypes}
              setSelectedVendorTypeIds={setSelectedVendorTypeIds}
              selectedVendorTypeIds={selectedVendorTypeIds}
              applyFilters={(clearFilters?: boolean) => {
                if (clearFilters) {
                  setFilters({
                    typeId: '',
                    vendorTypeIds: []
                  });
                } else {
                  setPage(0);
                  setFilters({
                    ...filters,
                    typeId: selectedTypeId ?? undefined,
                    vendorTypeIds: selectedVendorTypeIds ?? undefined
                  });
                }
              }}
              hasAppliedFilters={hasAllAppliedFilters}
              setHasAppliedFilters={setHasAppliedFilters}
              handleSearch={(val: string) => {
                setPage(0);
                setSearchedValue(val);
              }}
            />

            <Table
              columns={vendorColumns}
              data={vendors}
              isLoading={isLoadingVendors}
              hidePagination
              mobileProps={{
                handleEdit: (val: IVendor) => {
                  history.push(`/admin/vendor-tools/vendors/${val.vendorId}`);
                }
              }}
              ResponsiveComponent={isMobile ? MobileVendor : undefined}
            />
            {!isLoadingVendors && (
              <Pagination
                page={page}
                count={recordCount}
                rowsPerPage={perPage}
                setPage={setPage}
                setRowsPerPage={setRowsPerPage}
              />
            )}
          </>
        )}
        {selectedTab === 1 && (
          <>
            <Table
              columns={subcontractorsTypesColumns}
              data={subcontractorTypes}
              isLoading={isLoadingSubcontractorTypes}
              hidePagination
            />
            {!isLoadingSubcontractorTypes && (
              <Pagination
                page={typePage}
                count={recordTypeCount}
                rowsPerPage={typePerPage}
                setPage={setTypePage}
                setRowsPerPage={setTypeRowsPerPage}
              />
            )}
            <SubcontractorTypeModal
              onClose={() => {
                setModalOpen(false);
                setCurrentSubcontractorType(null);
              }}
              open={isModalOpen}
              fetchSubcontractorTypes={fetchSubcontractorTypes}
              currentSubcontractorType={currentSubcontractorType}
            />
          </>
        )}
        {selectedTab === 2 && (
          <>
            <Table
              columns={documentTypeColumns}
              data={documentTypes}
              isLoading={isLoadingDocumentTypes}
              hidePagination
            />
            {!isLoadingDocumentTypes && (
              <Pagination
                page={documentTypePage}
                count={recordDocumentTypeCount}
                rowsPerPage={documentTypePerPage}
                setPage={setDocumentTypePage}
                setRowsPerPage={setDocumentTypePerPage}
              />
            )}
            <DocumentTypeModal
              onClose={() => {
                setDocumentTypeModalOpen(false);
                setCurrentDocumentType(null);
              }}
              open={isDocumentTypeModalOpen}
              fetchDocumentTypes={fetchDocumentTypes}
              currentDocumentType={currentDocumentType}
            />
          </>
        )}
      </Page>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  actionButtons: {
    textAlign: 'center'
  },
  actionButtonTypes: {
    textAlign: 'right'
  },
  editButton: {
    textAlign: 'right'
  },
  deleteButton: {
    color: theme.palette.error.main
  },
  pageTitleWrapper: {
    '@media (min-width: 768px)': {
      flex: 1
    }
  },
  titleWrapper: {
    marginBottom: theme.spacing(0.5)
  },
  infoWrapper: {
    '@media (min-width: 768px)': {
      textAlign: 'right'
    }
  },
  tabBar: {
    backgroundColor: '#fff',
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.grey[400]}`
  },
  addButton: {
    alignSelf: 'flex-start',
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  },
  addDocumentButton: {
    alignSelf: 'flex-start',
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginTop: 0,
      width: 'auto'
    }
  }
}));
